// Plain-language explanations for the console's metric terms (#228 manual-test
// feedback: "hover to show what each metric means"). Keyed by the exact label
// rendered in the UI so the shared display primitives (StatCard / MiniStat /
// RatePanel / CardTitle) can auto-attach a tooltip via glossaryHint(label).
// A label with no entry simply renders without a tooltip — harmless.
export const GLOSSARY: Record<string, string> = {
  // Traffic
  "请求": "区间内 API 请求总数。",
  "请求量": "区间内 API 请求总数。",
  "请求（按小时）": "每小时的请求数量。",
  "状态码": "按 2xx / 4xx / 5xx 分类的响应数：2xx 成功、4xx 客户端错误、5xx 服务端错误。",
  "状态码分布": "按 2xx / 4xx / 5xx 分类的响应数：2xx 成功、4xx 客户端错误、5xx 服务端错误。",
  "热门接口": "被调用次数最多的 API 接口（请求方法 + 路径）。",
  "高峰时段": "按「星期 × 小时」统计的请求热力图，颜色越深表示该时段越繁忙。",
  "延迟": "服务端响应耗时的分位数（毫秒）。",
  "p50": "中位延迟：50% 的请求快于此值（毫秒）。",
  "p95": "95% 的请求快于此值（毫秒），反映较慢请求的体验。",
  "p99": "99% 的请求快于此值（毫秒），反映最慢的一批请求。",
  "p95 延迟趋势": "每个时间段内响应延迟 95 分位随时间的变化。",
  "总计": "区间内的请求总数。",

  // Errors
  "错误率": "5xx（或错误事件）占总请求的比例，越低越好。",
  "错误数": "区间内的错误数量。",
  "错误（区间）": "选定时间区间内的错误数量。",
  "错误率趋势": "5xx（或错误事件）占比随时间的变化。",

  // History vs current (#228)
  "当前（实时快照）": "此刻的实时状态，会随会话创建 / 删除而变化（不是历史）。",
  "历史（近 24 小时，不可变）": "来自不可变事件日志的历史计数，删除会话不会让它减少。",
  "当前在线": "最近有心跳（last_seen 在阈值内）的在线用户数，实时快照。",
  "活跃会话": "未删除、最近有过对话的会话数（实时）。",
  "空闲会话": "未删除、近期无对话的会话数（实时）。",
  "新建会话": "区间内新建的会话数，来自不可变事件日志，删除不会让它减少。",
  "删除会话": "区间内被（软）删除的会话数，来自不可变事件日志。",
  "错误": "区间内的错误数量。",
  "最近错误": "最近发生的错误（状态码 ≥ 500 或错误事件）。",
  "活跃用户": "按消息量排序的最活跃用户。",
  "会话新建 vs 删除趋势": "每个时间段内新建与（软）删除的会话数趋势。",
  "标注（评分 / 反馈）": "用户对回答的评分与反馈标注数量。",

  // Insights (CycleCare)
  "知识库命中率": "助手直接从知识库找到答案的对话占比，越高越好。",
  "知识库命中率（分层）": "按答案来源层级（缓存/高置信/低置信/联网/拒答）分解的知识库命中分布，来自 turn_completed 事件。",
  "联网兜底率": "知识库未命中、转而用联网搜索兜底回答的对话占比。数据来自 turn_completed.answerTier='web_fallback'，已修正 chat_outcome 中的错误分类（#228）。",
  "转人工率": "转交人工客服（创建工单）的对话占比。",
  "点踩率": "用户对回答点「踩」（负反馈）的占比，越低越好。",
  "拒答率": "助手判定问题超出范围而拒绝回答的占比。",
  // Answer-source tier labels (Phase 2)
  "缓存": "该轮回答由答案缓存（learned_knowledge）直接命中，未调用 LLM，token 消耗为 0。",
  "高置信": "知识库（search_kb）命中，置信度满足阈值，直接用于回答。",
  "低置信": "知识库（search_kb）命中但置信度低，回答不确定性较高。",
  "联网": "知识库低置信后转而通过联网搜索（web_search）兜底回答。",
  "拒答": "助手判定问题超出范围，拒绝回答。token 消耗为 0。",
  "其他": "未触发检索的对话（直接回答 / 闲聊等），不属于上述任一层级。",

  // Usage (Phase 3)
  "日活用户（DAU）": "所选时间窗内有过对话轮次的独立用户数，按 actor 全窗口去重（非单个时间段峰值）。下方折线为各时间段的活跃用户数。",
  "日活会话": "所选时间窗内有过对话轮次的独立会话数，按 sessionId 全窗口去重。下方折线为各时间段的活跃会话数。",
  "平均轮次/会话": "窗口内每个时间段的平均对话轮次数（轮次 / 去重会话数）。",
  "平均 token/轮": "每轮次的平均 token 消耗，含 prompt + completion token 合计，0-填充对齐。",
  "token 用量趋势": "每时间段的 token 消耗总量（sumTotalTokens），反映整体用量走势。",
  "输入 vs 输出 token": "每时间段的 prompt（输入）token 与 completion（输出）token 的对比。",
  "输入 token": "发送给 LLM 的 prompt token 数，含上下文与系统提示词。",
  "输出 token": "LLM 生成的 completion token 数（响应内容）。",
  "估算成本（¥）": "基于模型单价估算的人民币成本趋势（估算值，非精确账单）。",
  "估算成本": "基于每步骤 token 用量与模型单价估算的人民币成本，非精确账单。",
  "TTFT 与 p95 延迟": "首 token 时间（TTFT）与端到端 p95 延迟，反映响应速度体验。",
  "p50 TTFT": "首 token 时间的中位数（50% 的轮次首字到达快于此值，毫秒）。",
  "p95 TTFT": "首 token 时间的 95 分位（毫秒），反映较慢轮次的首字体验。",
  "缓存节省": "通过 prompt-cache 读取和答案缓存命中节省的 token 量与次数。",
  "缓存读取 token": "LLM 调用中从 prompt-cache 读取的 token 数，计费低于全量输入。",
  "缓存命中次数": "答案缓存（learned_knowledge）命中、跳过 LLM 调用的次数。",
  "估算节省 token": "答案缓存命中次数 × 同期非缓存轮次的平均 token 数（估算，见「缓存节省·估算」）。",
  "缓存节省·估算": "节省量 = 缓存命中次数 × 同期非缓存轮次均值，为估算；精确版需记录原始命中轮次 token 数。",
  "估算": "基于统计均值或模型单价推算，非逐条精确核算。",
  "用户 token 用量": "窗口内各用户的 token 消耗合计，按降序排列；用于识别重度用量用户。",
  "用户 token 用量（近 7 天 Top）": "过去 7 天内按总 token 消耗排名的活跃用户，来自用量 API topUsers 字段。",
  "知识缓存（分层）": "学习型知识缓存（LKC）：二级=联网检索结果缓存，三级=对话/转人工沉淀的知识。命中即跳过重新检索/研究，省 token。",
  "二级知识库": "联网检索结果的缓存（learned_knowledge, source=web）；相似问题再次出现时直接复用，省去重新联网与生成。",
  "三级知识库": "从对话与转人工中沉淀的知识（learned_knowledge, source=chat/handoff）；越用越懂，命中即复用。",

  // Users
  "用户数": "用户账户总数。",
  "用户": "用户账户数（当前页）。",
  "会话数": "会话（对话）总数。",
  "当前会话": "未删除的会话数（实时快照，删除会话后减少）。",
  "历史会话": "累计创建的会话数（来自不可变事件日志，永不回退）。",
  "消息数": "消息总数。",
  "点踩数": "收到「踩」负反馈的数量。",
  "差评反馈": "收到「踩」负反馈的数量。",
  "用户活动": "用户列表：在线状态、当前 / 历史会话、消息与差评。",

  // System
  "CPU": "主机 CPU 使用率。",
  "内存": "主机内存使用率。",
  "磁盘": "主机磁盘使用率。",
  "运行时长": "服务进程自启动以来的运行时间。",
  "事件循环延迟": "Node.js 事件循环滞后，数值偏高说明主线程被阻塞。",
  "连接池": "数据库连接池状态（空闲 / 总数 / 等待）。",
  "数据库连接池": "数据库连接池状态（空闲 / 总数 · 等待）。",
  "主机指标": "主机 CPU / 内存 / 磁盘等资源使用情况。",
  "服务": "外部依赖服务（如向量库、搜索）的健康状态与延迟。",

  // Other
  "数据库备份": "数据库定时备份的运行记录。"
};

/** The explanation for a metric label, or undefined if none is registered. */
export function glossaryHint(term: string): string | undefined {
  return GLOSSARY[term.trim()];
}
